// services/sportsApi.ts
import { UpcomingGame, Team, Game, StatItem } from '../types';
import { getCached, setCached } from './cache';

const LEAGUE_IDS: Record<string, string> = {
  NBA: '4387',
  MLB: '4424',
  NFL: '4391',
  NHL: '4380',
};

const API_BASE = 'https://www.thesportsdb.com/api/v1/json/123';

export interface SportsApiGame extends UpcomingGame {
  awayScore?: number;
  homeScore?: number;
  status: 'upcoming' | 'final';
  timestamp?: string;
}

function parseScore(score: unknown): number | undefined {
  if (score === null || score === undefined || score === '') return undefined;

  const parsed = Number(score);
  return Number.isNaN(parsed) ? undefined : parsed;
}

function mapEventToGame(event: any, league: string, status: SportsApiGame['status']): SportsApiGame {
  return {
    id: event.idEvent,
    league,
    date: event.dateEvent,
    time: event.strTime,
    location: event.strVenue,
    homeTeam: {
      id: event.idHomeTeam,
      name: event.strHomeTeam,
      logo: event.strHomeTeamBadge || '',
    },
    awayTeam: {
      id: event.idAwayTeam,
      name: event.strAwayTeam,
      logo: event.strAwayTeamBadge || '',
    },
    eventName: event.strEvent,
    awayScore: parseScore(event.intAwayScore),
    homeScore: parseScore(event.intHomeScore),
    status,
    timestamp: event.strTimestamp || undefined,
  };
}

export async function fetchUpcomingGames(league: string): Promise<SportsApiGame[]> {
  const cacheKey = `upcoming_${league}`;
  const cachedGames = getCached<SportsApiGame[]>(cacheKey);
  if (cachedGames) {
    return cachedGames;
  }

  const leagueId = LEAGUE_IDS[league];
  if (!leagueId) throw new Error(`Unsupported league: ${league}`);

  const url = `${API_BASE}/eventsnextleague.php?id=${leagueId}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error('Failed to fetch upcoming games');

  const data = await res.json();
  if (!data.events) return [];

  const games = data.events.map((event: any) => mapEventToGame(event, league, 'upcoming'));
  setCached(cacheKey, games);

  return games;
}

export async function fetchRecentResults(league: string): Promise<SportsApiGame[]> {
  const cacheKey = `recent_${league}`;
  const cachedGames = getCached<SportsApiGame[]>(cacheKey);
  if (cachedGames) {
    return cachedGames;
  }

  const leagueId = LEAGUE_IDS[league];
  if (!leagueId) throw new Error(`Unsupported league: ${league}`);

  const url = `${API_BASE}/eventspastleague.php?id=${leagueId}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error('Failed to fetch recent results');

  const data = await res.json();
  if (!data.events) return [];

  const games = data.events.map((event: any) => mapEventToGame(event, league, 'final'));
  setCached(cacheKey, games);

  return games;
}

export async function searchTeams(query: string): Promise<Team[]> {
  const url = `${API_BASE}/searchteams.php?t=${query}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error('Failed to search for teams');
  const data = await res.json();
  if (!data.teams) return [];
  return data.teams.map((team: any) => ({
    id: team.idTeam,
    name: team.strTeam,
    logo: team.strTeamBadge,
  }));
}

export async function fetchTeamPastGames(teamId: string): Promise<Game[]> {
    const cacheKey = `pastgames_${teamId}`;
    const cachedGames = getCached<Game[]>(cacheKey);
    if (cachedGames) {
        return cachedGames;
    }

    const url = `${API_BASE}/eventsseason.php?id=${teamId}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error('Failed to fetch past games');
    const data = await res.json();
    if (!data.events) return [];

    const games = data.events.map((game: any) => ({
        id: game.idEvent,
        opponent: game.strOpponent,
        result: game.intHomeScore > game.intAwayScore ? 'W' : 'L',
        score: `${game.intHomeScore}-${game.intAwayScore}`,
    }));

    setCached(cacheKey, games);
    return games;
}

export async function fetchGameStatistics(eventId: string): Promise<StatItem[]> {
    const cacheKey = `statistics_${eventId}`;
    const cachedStats = getCached<StatItem[]>(cacheKey);
    if (cachedStats) {
        return cachedStats;
    }

    const url = `${API_BASE}/eventstatistics.php?id=${eventId}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error('Failed to fetch game statistics');
    const data = await res.json();
    if (!data.statistics) return [];

    const stats = data.statistics.map((stat: any) => ({
        name: stat.strStat,
        team1Value: stat.intHomeValue,
        team2Value: stat.intAwayValue,
    }));

    setCached(cacheKey, stats);
    return stats;
}
